import type {
  AddedToCartObjectIDsAfterSearchParams,
  AddedToCartObjectIDsParams,
  ClickedFiltersParams,
  ClickedObjectIDsAfterSearchParams,
  ClickedObjectIDsParams,
  ConvertedObjectIDsAfterSearchParams,
  ConvertedObjectIDsParams,
  PurchasedObjectIDsAfterSearchParams,
  ViewedObjectIDsParams,
} from '@maestro/algolia-insights/types'

import aa from 'search-insights'

import { createLogger } from '@maestro/core/logger'

const log = createLogger('maestro:algolia-insights')

/**
 * Initialize Algolia Insights
 */
export function initInsights(
  appId: string,
  apiKey: string,
  isDebug = false
): void {
  try {
    aa('init', {
      appId,
      apiKey,
      useCookie: false, // We manage our own user token
    })

    if (isDebug) {
      log.info('[Algolia Insights] Initialized with appId:', appId)
    }
  } catch (error) {
    log.error('[Algolia Insights] Failed to initialize:', error)
  }
}

/**
 * Generic function to send Algolia Insights events
 * All event-sending functions use this to reduce code duplication
 */
function sendAlgoliaEvent<T>(
  eventMethod: string,
  params: T,
  isDebug = false
): void {
  try {
    if (isDebug) {
      log.info(`[Algolia Insights] ${eventMethod}:`, params)
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    aa(eventMethod as any, params)
  } catch (error) {
    log.error(`[Algolia Insights] Failed to send ${eventMethod}:`, error)
  }
}

/**
 * Send viewedObjectIDs event to Algolia
 * Tracks when users view products (e.g., in search results or product detail pages)
 */
export function viewedObjectIDs(
  params: ViewedObjectIDsParams,
  isDebug = false
): void {
  sendAlgoliaEvent('viewedObjectIDs', params, isDebug)
}

/**
 * Send clickedObjectIDsAfterSearch event to Algolia
 * Tracks when users click products from search results
 */
export function clickedObjectIDsAfterSearch(
  params: ClickedObjectIDsAfterSearchParams,
  isDebug = false
): void {
  sendAlgoliaEvent('clickedObjectIDsAfterSearch', params, isDebug)
}

/**
 * Send clickedObjectIDs event to Algolia (without search context)
 * Tracks when users click products without search attribution
 */
export function clickedObjectIDs(
  params: ClickedObjectIDsParams,
  isDebug = false
): void {
  sendAlgoliaEvent('clickedObjectIDs', params, isDebug)
}

/**
 * Send convertedObjectIDsAfterSearch event to Algolia
 * Tracks when users add products to cart from search results
 */
export function convertedObjectIDsAfterSearch(
  params: ConvertedObjectIDsAfterSearchParams,
  isDebug = false
): void {
  sendAlgoliaEvent('convertedObjectIDsAfterSearch', params, isDebug)
}

/**
 * Send convertedObjectIDs event to Algolia (without search context)
 * Tracks when users add products to cart without search attribution
 */
export function convertedObjectIDs(
  params: ConvertedObjectIDsParams,
  isDebug = false
): void {
  sendAlgoliaEvent('convertedObjectIDs', params, isDebug)
}

/**
 * Send purchasedObjectIDsAfterSearch event to Algolia
 * Tracks when users complete purchases with search attribution
 */
export function purchasedObjectIDsAfterSearch(
  params: PurchasedObjectIDsAfterSearchParams,
  isDebug = false
): void {
  sendAlgoliaEvent('purchasedObjectIDsAfterSearch', params, isDebug)
}

/**
 * Send addedToCartObjectIDsAfterSearch event to Algolia
 * Tracks when users add products to cart from search results
 */
export function addedToCartObjectIDsAfterSearch(
  params: AddedToCartObjectIDsAfterSearchParams,
  isDebug = false
): void {
  sendAlgoliaEvent('addedToCartObjectIDsAfterSearch', params, isDebug)
}

/**
 * Send addedToCartObjectIDs event to Algolia (without search context)
 * Tracks when users add products to cart without search attribution
 */
export function addedToCartObjectIDs(
  params: AddedToCartObjectIDsParams,
  isDebug = false
): void {
  sendAlgoliaEvent('addedToCartObjectIDs', params, isDebug)
}

/**
 * Send clickedFilters event to Algolia
 * Tracks when users click on filters
 */
export function clickedFilters(
  params: ClickedFiltersParams,
  isDebug = false
): void {
  sendAlgoliaEvent('clickedFilters', params, isDebug)
}
